import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ExpandMore,
  Add,
  Delete,
  Save,
  Send,
  Info,
  AcUnit,
  Thermostat,
  PhotoCamera,
  Draw,
  CheckCircle,
  Person,
  Business,
  Settings,
  ElectricBolt,
  DeviceThermostat,
  Weekend,
  Inventory,
  Science,
  Visibility,
  Air,
  Cancel
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SignatureCanvas from '../../components/SignatureCanvas';
import ImageUploader from '../../components/ImageUploader';
import AppHeader from '../../components/AppHeader';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

// Helper function to capitalize first letter of each word
const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export default function ProtocolFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const customer = location.state?.customer;
  const editFormData = location.state?.formData;
  const templateData = location.state?.templateData;  // NEU: Template-Modus
  const editMode = location.state?.editMode;
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [autoSavedData, setAutoSavedData] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const [error, setError] = useState('');
  const [lastAutoSaveTime, setLastAutoSaveTime] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const [expanded, setExpanded] = useState('panel1');
  
  // Refs für erstes Feld in jedem Accordion
  const panel1FirstFieldRef = useRef(null);
  const panel2FirstFieldRef = useRef(null);
  const panel3FirstFieldRef = useRef(null);
  const panel4FirstFieldRef = useRef(null);
  const panel5FirstFieldRef = useRef(null);
  
  const [herstellerList, setHerstellerList] = useState([
    'Kaysun', 'Mitsubishi', 'Daikin', 'Panasonic', 'LG', 'Samsung', 
    'Toshiba', 'Fujitsu', 'Hitachi', 'Sonstiger'
  ]);
  
  // Signature Dialogs
  const [monteurSigDialog, setMonteurSigDialog] = useState(false);
  const [kundeSigDialog, setKundeSigDialog] = useState(false);

  // Determine data source: template > edit > customer > default
  const sourceData = templateData || editFormData;
  
  // Debug: Beim Laden prüfen, ob Bilder vorhanden sind
  useEffect(() => {
    if (sourceData) {
      console.log('🔍 sourceData geladen:', {
        id: sourceData._id,
        status: sourceData.status,
        bilderAnzahl: sourceData.bilder?.length || 0,
        bilderVorhanden: !!sourceData.bilder,
        ersteBildKeys: sourceData.bilder?.[0] ? Object.keys(sourceData.bilder[0]) : 'keine'
      });
    }
  }, [sourceData]);

  // Form Data
  const [formData, setFormData] = useState({
    // Allgemeine Informationen
    datum: sourceData?.datum || new Date().toISOString().split('T')[0],
    anlagen_id: sourceData?.anlagen_id || '',
    auftragsnummer: sourceData?.auftragsnummer || '',
    kunde_name: customer?.name || sourceData?.kunde_name || '',
    ku_nr: customer?.ku_nr || sourceData?.ku_nr || '',
    kunde_email: customer?.email || sourceData?.kunde_email || '',
    standort: sourceData?.standort || '',
    hersteller: sourceData?.hersteller || 'Kaysun',  // Standard: Kaysun
    hersteller_sonstiger: sourceData?.hersteller_sonstiger || '',  // NEU
    anzahl_innengeraete: sourceData?.anzahl_innengeraete || 1,
    
    // Innengeräte (Array)
    innengeraete: sourceData?.innengeraete || [{
      montageort: '',  // NEU
      modell: '',
      seriennummer: '',
      betriebsmodus: 'kühlen',  // NEU: kühlen oder heizen
      ansaug_temp: '',
      ausblas_temp: '',
      luefterstufe: '',  // NEU: Lüfterstufe 1-7 oder Automatik
      arbeiten: {
        luftfilter: false,
        verdampfer: false,
        kondenswasserpumpe: false,
        kondenswasserwanne: false,
        kondenswasserablauf_geprueft: false,  // NEU
        elektrische_anschluesse: false,
        geblaesemotor: false,
        ausblaslamellen: false,
        gehaeuse: false
      }
    }],
    
    // Außengerät
    aussengeraet: {
      modell: sourceData?.aussengeraet?.modell || '',
      seriennummer: sourceData?.aussengeraet?.seriennummer || '',
      aussen_temp: sourceData?.aussengeraet?.aussen_temp || '',
      stromaufnahme: sourceData?.aussengeraet?.stromaufnahme || '',
      arbeiten: sourceData?.aussengeraet?.arbeiten || {
        waermetauscher: false,
        kaeltetechnische_anschluesse: false,
        kondensatwanne: false,
        innenraum: false,
        lueftermotor: false,
        isolierung: false,
        elektrische_anschluesse: false,
        sicherheitseinrichtungen: false,
        kompressor: false,
        kaeltemitteldruecke: false,
        leistungsmessung: false
      }
    },
    
    // Dichteprüfung & Kältemittel
    dichtepr_notwendig: sourceData?.dichtepr_notwendig || 'nein',  // Standard: nein
    dichtepr_durchgefuehrt: sourceData?.dichtepr_durchgefuehrt || 'nein',  // NEU
    pruefintervall: sourceData?.pruefintervall || '12',
    anlage_status: sourceData?.anlage_status || 'dicht',
    kaeltemittel: sourceData?.kaeltemittel || 'R32',
    fuellmenge_typenschild: sourceData?.fuellmenge_typenschild || '',
    nachfuellmenge: sourceData?.nachfuellmenge || '0',
    gesamtfuellmenge: sourceData?.gesamtfuellmenge || '',
    
    // Bilder
    bilder: sourceData?.bilder || [],  // NEU: max 5 Bilder
    
    // Hinweise & Unterschriften
    hinweise: sourceData?.hinweise || '',
    monteur_unterschrift: sourceData?.monteur_unterschrift || '',
    kunde_zufrieden: sourceData?.kunde_zufrieden || 'ja',  // String: 'ja' oder 'nein'
    kunde_zufrieden_begruendung: sourceData?.kunde_zufrieden_begruendung || '',
    kunde_name_unterschrift: sourceData?.kunde_name_unterschrift || '',  // NEU
    kunde_unterschrift: sourceData?.kunde_unterschrift || '',
    
    monteur: sourceData?.monteur || user?.full_name || user?.username || '',
    status: sourceData?.status || 'draft'
  });

  // AUTO-SAVE FUNKTIONEN
  const AUTO_SAVE_KEY = editMode && editFormData?._id 
    ? `klima_protocol_autosave_edit_${editFormData._id}`
    : 'klima_protocol_autosave';
  const AUTO_SAVE_EXPIRY = 24 * 60 * 60 * 1000; // 24 Stunden in Millisekunden

  // Beim Laden: Prüfe ob Auto-Save vorhanden
  useEffect(() => {
    const savedData = localStorage.getItem(AUTO_SAVE_KEY);
    const TEMPLATE_SESSION_KEY = 'klima_template_session';
    
    // Template-Modus: Markiere in SessionStorage (überlebt Re-Mounts in gleicher Session)
    if (templateData) {
      const existingSession = sessionStorage.getItem(TEMPLATE_SESSION_KEY);
      
      if (!existingSession) {
        // Erstes Mal in dieser Session - Markiere als Template-Session
        sessionStorage.setItem(TEMPLATE_SESSION_KEY, Date.now().toString());
        
        if (savedData) {
          localStorage.removeItem(AUTO_SAVE_KEY);
          console.log('🔄 Auto-Save: Template geladen, altes Auto-Save gelöscht');
        } else {
          console.log('📋 Template-Modus: Kein altes Auto-Save vorhanden');
        }
      } else {
        console.log('📋 Template-Session läuft bereits, Auto-Save erlaubt');
      }
      return;
    }

    // Kein Template-Modus: Lösche Template-Session-Marker
    sessionStorage.removeItem(TEMPLATE_SESSION_KEY);

    // Prüfe Auto-Save
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const now = Date.now();
        const age = now - parsed.timestamp;

        // Prüfe ob < 24h alt
        if (age < AUTO_SAVE_EXPIRY) {
          setAutoSavedData(parsed.data);
          setShowRestoreDialog(true);
        } else {
          // Zu alt, lösche Auto-Save
          localStorage.removeItem(AUTO_SAVE_KEY);
        }
      } catch (e) {
        console.error('Auto-Save konnte nicht geladen werden:', e);
        localStorage.removeItem(AUTO_SAVE_KEY);
      }
    }
  }, [AUTO_SAVE_KEY, templateData]);

  // Auto-Save: Speichere Formulardaten (debounced)
  useEffect(() => {
    // Debounce: Warte 3 Sekunden nach letzter Änderung
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    setAutoSaveStatus('Wird gespeichert...');

    autoSaveTimerRef.current = setTimeout(() => {
      // Nur speichern wenn Formulardaten vorhanden
      const hasContent = formData.kunde_name || formData.auftragsnummer || (formData.innengeraete && formData.innengeraete[0]?.modell);
      
      const modus = editMode && editFormData?._id ? `Edit (ID: ${editFormData._id})` : 'Neu';
      console.log(`📋 Auto-Save Check [${modus}]:`, {
        key: AUTO_SAVE_KEY,
        kunde_name: formData.kunde_name,
        auftragsnummer: formData.auftragsnummer,
        hasInnengeraete: !!formData.innengeraete,
        firstModell: formData.innengeraete?.[0]?.modell,
        hasContent
      });

      if (hasContent) {
        try {
          const dataToSave = {
            data: formData,
            timestamp: Date.now()
          };
          localStorage.setItem(AUTO_SAVE_KEY, JSON.stringify(dataToSave));
          const now = new Date();
          const timeStr = now.toLocaleTimeString('de-DE');
          setLastAutoSaveTime(timeStr);
          setAutoSaveStatus(`Gespeichert um ${timeStr}`);
          console.log(`💾 Auto-Save gespeichert [${modus}] um ${timeStr} → Key: ${AUTO_SAVE_KEY}`);
        } catch (e) {
          console.error('❌ Auto-Save Fehler:', e);
          setAutoSaveStatus('Fehler beim Speichern');
        }
      } else {
        console.log('⏭️ Auto-Save: Keine Daten zum Speichern');
        setAutoSaveStatus('');
      }
    }, 3000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [formData, AUTO_SAVE_KEY, editMode, editFormData]);

  // Lösche Auto-Save nach erfolgreichem Submit
  const clearAutoSave = () => {
    localStorage.removeItem(AUTO_SAVE_KEY);
    console.log('🗑️ Auto-Save gelöscht');
  };

  // Stelle Auto-Save wieder her
  const handleRestoreAutoSave = () => {
    if (autoSavedData) {
      setFormData(autoSavedData);
      setShowRestoreDialog(false);
      alert('Ihr begonnenes Formular wurde wiederhergestellt!');
    }
  };

  // Verwerfe Auto-Save
  const handleDiscardAutoSave = () => {
    clearAutoSave();
    setShowRestoreDialog(false);
    setAutoSavedData(null);
  };

  // Handle accordion change
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
    
    // Fokussiere das erste Feld des geöffneten Accordions
    if (isExpanded) {
      setTimeout(() => {
        switch(panel) {
          case 'panel1':
            panel1FirstFieldRef.current?.focus();
            break;
          case 'panel2':
            panel2FirstFieldRef.current?.focus();
            break;
          case 'panel3':
            panel3FirstFieldRef.current?.focus();
            break;
          case 'panel4':
            panel4FirstFieldRef.current?.focus();
            break;
          case 'panel5':
            panel5FirstFieldRef.current?.focus();
            break;
          default:
            break;
        }
      }, 300); // Kleine Verzögerung für die Accordion-Animation
    }
  };
  
  // Helper: Capitalize first letter
  const capitalizeFirst = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Handle Bilder
  const handleImagesSelected = (images) => {
    setFormData(prev => ({
      ...prev,
      bilder: images
    }));
  };

  // Handle nested field changes (Außengerät)
  const handleAussengeraetChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      aussengeraet: {
        ...prev.aussengeraet,
        [field]: value
      }
    }));
  };

  // Handle Außengerät Arbeiten Checkboxes
  const handleAussengeraetArbeitenChange = (field) => {
    setFormData(prev => ({
      ...prev,
      aussengeraet: {
        ...prev.aussengeraet,
        arbeiten: {
          ...prev.aussengeraet.arbeiten,
          [field]: !prev.aussengeraet.arbeiten[field]
        }
      }
    }));
  };

  // Handle Innengerät changes
  const handleInnengeraetChange = (index, field, value) => {
    const newInnengeraete = [...formData.innengeraete];
    newInnengeraete[index] = {
      ...newInnengeraete[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      innengeraete: newInnengeraete
    }));
  };

  // Prüfe ob Temperaturen realistisch sind
  const checkTemperatureLogic = (geraet) => {
    const ansaug = parseFloat(geraet.ansaug_temp);
    const ausblas = parseFloat(geraet.ausblas_temp);
    const betriebsmodus = geraet.betriebsmodus || 'kühlen';
    
    if (!ansaug || !ausblas) {
      return null;
    }
    
    if (betriebsmodus === 'kühlen') {
      // Beim Kühlen muss Ausblas < Ansaug sein
      if (ausblas >= ansaug) {
        return 'Im Kühlbetrieb muss die Ausblastemperatur niedriger sein als die Ansaugtemperatur!';
      }
    } else {
      // Beim Heizen muss Ausblas > Ansaug sein
      if (ausblas <= ansaug) {
        return 'Im Heizbetrieb muss die Ausblastemperatur höher sein als die Ansaugtemperatur!';
      }
    }
    
    return null;
  };

  // Berechne Effizienzeinschätzung basierend auf Temperaturdifferenz
  const calculateEfficiency = (geraet) => {
    const ansaug = parseFloat(geraet.ansaug_temp);
    const ausblas = parseFloat(geraet.ausblas_temp);
    const luefterstufe = geraet.luefterstufe;
    
    // Prüfe ob alle Werte vorhanden sind
    if (!ansaug || !ausblas || !luefterstufe) {
      return null;
    }
    
    // Prüfe ob höchste Lüfterstufe verwendet wird
    const hoechsteStufen = ['Stufe 3', 'Stufe 4', 'Stufe 5', 'Stufe 7', 'Automatik'];
    if (!hoechsteStufen.includes(luefterstufe)) {
      return null; // Keine Bewertung bei niedrigen Stufen
    }
    
    // Berechne Temperaturdifferenz (absolut)
    const deltaT = Math.abs(ausblas - ansaug);
    const betriebsmodus = geraet.betriebsmodus || 'kühlen';
    
    let status = 'gut';
    let color = '#4caf50'; // Grün
    let sollbereich = '';
    
    if (betriebsmodus === 'kühlen') {
      sollbereich = '6-9 K';
      if (deltaT > 9) {
        status = 'sehr gut';
        color = '#4caf50';
      } else if (deltaT >= 6) {
        status = 'gut';
        color = '#4caf50';
      } else if (deltaT >= 4) {
        status = 'ausreichend';
        color = '#ff9800'; // Orange
      } else {
        status = 'mangelhaft';
        color = '#f44336'; // Rot
      }
    } else { // heizen
      sollbereich = '8-12 K';
      if (deltaT > 12) {
        status = 'sehr gut';
        color = '#4caf50';
      } else if (deltaT >= 8) {
        status = 'gut';
        color = '#4caf50';
      } else if (deltaT >= 5) {
        status = 'ausreichend';
        color = '#ff9800';
      } else {
        status = 'mangelhaft';
        color = '#f44336';
      }
    }
    
    return {
      status,
      color,
      deltaT: deltaT.toFixed(1),
      sollbereich,
      betriebsmodus
    };
  };

  // Format Auftragsnummer: XXXX/YY (z.B. 0001/26, 0028/26, 0321/26, 1234/26)
  // Formatierung erfolgt beim Verlassen des Feldes (onBlur)
  const handleAuftragsnummerChange = (value) => {
    // Entferne alles außer Zahlen und /
    const cleaned = value.replace(/[^\d/]/g, '');
    setFormData(prev => ({ ...prev, auftragsnummer: cleaned }));
  };

  // Formatierung beim Verlassen des Feldes
  const handleAuftragsnummerBlur = () => {
    const value = formData.auftragsnummer;
    // Extrahiere nur Zahlen
    const numbers = value.replace(/[^\d]/g, '');
    
    if (numbers.length === 0) {
      return;
    }
    
    // Begrenze auf maximal 4 Ziffern
    const limitedNumbers = numbers.slice(0, 4);
    
    // Formatierung: Immer 4 Stellen mit führenden Nullen
    // 1 → 0001/26, 28 → 0028/26, 321 → 0321/26, 1234 → 1234/26
    if (limitedNumbers.length >= 1) {
      const currentYear = new Date().getFullYear().toString().slice(-2);
      // Fülle mit führenden Nullen auf 4 Stellen auf
      const paddedNumbers = limitedNumbers.padStart(4, '0');
      const formatted = `${paddedNumbers}/${currentYear}`;
      setFormData(prev => ({ ...prev, auftragsnummer: formatted }));
    }
  };

  // Handle Innengerät Arbeiten Checkboxes
  const handleInnengeraetArbeitenChange = (index, field) => {
    const newInnengeraete = [...formData.innengeraete];
    newInnengeraete[index] = {
      ...newInnengeraete[index],
      arbeiten: {
        ...newInnengeraete[index].arbeiten,
        [field]: !newInnengeraete[index].arbeiten[field]
      }
    };
    setFormData(prev => ({
      ...prev,
      innengeraete: newInnengeraete
    }));
  };

  // Handle Enter key for checkboxes
  const handleCheckboxKeyDown = (e, onToggle) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onToggle();
    }
  };

  // Add new Innengerät
  const handleAddInnengeraet = () => {
    if (formData.innengeraete.length >= 20) {
      alert('Maximal 20 Innengeräte erlaubt');
      return;
    }
    setFormData(prev => ({
      ...prev,
      innengeraete: [...prev.innengeraete, {
        montageort: '',  // NEU
        modell: '',
        seriennummer: '',
        betriebsmodus: 'kühlen',  // NEU: Standard Kühlbetrieb
        ansaug_temp: '',
        ausblas_temp: '',
        luefterstufe: '',  // NEU: Lüfterstufe 1-7 oder Automatik
        arbeiten: {
          luftfilter: false,
          verdampfer: false,
          kondenswasserpumpe: false,
          kondenswasserwanne: false,
          kondenswasserablauf_geprueft: false,  // NEU
          elektrische_anschluesse: false,
          geblaesemotor: false,
          ausblaslamellen: false,
          gehaeuse: false
        }
      }]
    }));
    
    // Auto-open neues Accordion und setze Focus auf Montageort
    setTimeout(() => {
      setExpanded('panel2');
      const newIndex = formData.innengeraete.length;
      const montageortInput = document.getElementById(`montageort-${newIndex}`);
      if (montageortInput) {
        montageortInput.focus();
      }
    }, 100);
  };

  // Remove Innengerät
  const handleRemoveInnengeraet = (index) => {
    if (formData.innengeraete.length === 1) {
      alert('Mindestens 1 Innengerät erforderlich');
      return;
    }
    const newInnengeraete = formData.innengeraete.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      innengeraete: newInnengeraete
    }));
  };

  // Calculate Gesamtfüllmenge
  useEffect(() => {
    const typenschild = parseFloat(formData.fuellmenge_typenschild) || 0;
    const nachfuell = parseFloat(formData.nachfuellmenge) || 0;
    const gesamt = (typenschild + nachfuell).toFixed(2);
    setFormData(prev => ({
      ...prev,
      gesamtfuellmenge: gesamt
    }));
  }, [formData.fuellmenge_typenschild, formData.nachfuellmenge]);

  // Update Anzahl Innengeräte when array changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      anzahl_innengeraete: prev.innengeraete.length
    }));
  }, [formData.innengeraete.length]);

  // Handle Save Draft
  // Handle Submit
  const handleSubmit = async () => {
    // Validation
    if (!formData.kunde_name) {
      setError('Bitte Kundenname eingeben');
      setExpanded('panel1');
      return;
    }
    if (!formData.auftragsnummer) {
      setError('Bitte Auftragsnummer eingeben');
      setExpanded('panel1');
      return;
    }
    if (!formData.monteur_unterschrift) {
      setError('Bitte Monteur-Unterschrift eintragen');
      setExpanded('panel5');
      return;
    }
    if (!formData.kunde_unterschrift) {
      setError('Bitte Kunden-Unterschrift eintragen');
      setExpanded('panel5');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        status: 'submitted'
      };

      let response;
      if (editMode && editFormData?._id) {
        response = await axios.put(
          `${API_URL}/klima-protocols/${editFormData._id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(
          `${API_URL}/klima-protocols`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      clearAutoSave(); // Lösche Auto-Save nach erfolgreichem Absenden
      alert('Wartungsprotokoll erfolgreich versendet!');
      navigate('/protocol/klima-list');
    } catch (err) {
      console.error('Error submitting protocol:', err);
      setError('Fehler beim Versenden: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        status: 'draft'
      };
      
      console.log('💾 Speichere Entwurf mit', formData.bilder?.length || 0, 'Bildern');

      let response;
      if (editMode && editFormData?._id) {
        response = await axios.put(
          `${API_URL}/klima-protocols/${editFormData._id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(
          `${API_URL}/klima-protocols`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      clearAutoSave(); // Lösche Auto-Save nach erfolgreichem Speichern
      alert('Entwurf erfolgreich gespeichert!');
      navigate('/protocol/klima-list');
    } catch (err) {
      console.error('Error saving draft:', err);
      setError('Fehler beim Speichern: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        status: 'draft'
      };

      // Call preview endpoint
      const response = await axios.post(
        `${API_URL}/protocols/preview`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.pdf_id) {
        // Open PDF in new tab
        const pdfUrl = `${API_URL}/protocols/preview/${response.data.pdf_id}`;
        window.open(pdfUrl, '_blank');
      }
    } catch (err) {
      console.error('Error generating preview:', err);
      setError('Fehler bei PDF-Vorschau: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <AppHeader title="Wartungsprotokoll Klimaanlage" showBack={true} backTo="/protocol/customer-search" />
      
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Auto-Save Status Indikator */}
        {autoSaveStatus && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 2,
              py: 0.5,
              '& .MuiAlert-message': {
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }
            }}
            icon={<span>💾</span>}
          >
            <strong>Auto-Speicherung{editMode ? ' (Entwurf)' : ''}:</strong> {autoSaveStatus}
          </Alert>
        )}

        {/* Accordion 1: Allgemeine Informationen */}
        <Accordion 
          expanded={expanded === 'panel1'} 
          onChange={handleAccordionChange('panel1')}
          sx={{ 
            mb: 2,
            boxShadow: 3,
            '&:before': { display: 'none' },
            borderRadius: '8px !important'
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              backgroundColor: '#1976d2',
              color: 'white',
              borderRadius: '8px 8px 0 0',
              '&:hover': { backgroundColor: '#1565c0' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>1. Allgemeine Informationen</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'white' }}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Datum"
                  type="date"
                  value={formData.datum}
                  onChange={(e) => handleChange('datum', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputRef={panel1FirstFieldRef}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Anlagen-ID"
                  value={formData.anlagen_id}
                  onChange={(e) => handleChange('anlagen_id', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  required
                  label="Auftragsnummer"
                  value={formData.auftragsnummer}
                  onChange={(e) => handleAuftragsnummerChange(e.target.value)}
                  onBlur={handleAuftragsnummerBlur}
                  placeholder="z.B. 321"
                  sx={{ 
                    width: 200,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#FFE5B4',
                      '&:hover': {
                        backgroundColor: '#FFD699'
                      },
                      '&.Mui-focused': {
                        backgroundColor: '#FFEDCC'
                      }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Kundenname"
                  value={formData.kunde_name}
                  onChange={(e) => handleChange('kunde_name', capitalizeFirst(e.target.value))}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Kundennummer"
                  value={formData.ku_nr}
                  onChange={(e) => handleChange('ku_nr', e.target.value)}
                  disabled={!!customer}
                  helperText={customer ? "Von Kundensuche übernommen" : ""}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Standort der Anlage"
                  value={formData.standort}
                  onChange={(e) => handleChange('standort', capitalizeFirst(e.target.value))}
                  sx={{ minWidth: 200 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Hersteller</InputLabel>
                  <Select
                    value={formData.hersteller}
                    label="Hersteller"
                    onChange={(e) => handleChange('hersteller', e.target.value)}
                    sx={{ minWidth: 200 }}
                  >
                    {herstellerList.map((h) => (
                      <MenuItem key={h} value={h}>{h}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Sonstiger Hersteller Textfeld */}
              {formData.hersteller === 'Sonstiger' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Hersteller (Sonstiger)"
                    value={formData.hersteller_sonstiger}
                    onChange={(e) => handleChange('hersteller_sonstiger', e.target.value.toUpperCase())}
                    sx={{ minWidth: 200 }}
                  />
                </Grid>
              )}
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Anzahl Innengeräte"
                  type="number"
                  value={formData.innengeraete.length}
                  disabled
                  InputProps={{ readOnly: true }}
                  sx={{ minWidth: 200 }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Accordion 2: Innengeräte */}
        <Accordion 
          expanded={expanded === 'panel2'} 
          onChange={handleAccordionChange('panel2')}
          sx={{ 
            mb: 2,
            boxShadow: 3,
            '&:before': { display: 'none' },
            borderRadius: '8px !important'
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              backgroundColor: '#2e7d32',
              color: 'white',
              borderRadius: '8px 8px 0 0',
              '&:hover': { backgroundColor: '#1b5e20' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AcUnit />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>2. Innengeräte ({formData.innengeraete.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'white' }}>
            <Box>
              {formData.innengeraete.map((geraet, index) => (
                <Card 
                  key={index} 
                  sx={{ 
                    mb: 3, 
                    border: '2px solid #2e7d32',
                    borderRadius: 2,
                    boxShadow: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      boxShadow: 4,
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      mb: 3,
                      pb: 2,
                      borderBottom: '2px solid #e0e0e0'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AcUnit sx={{ color: '#2e7d32' }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                          Innengerät #{index + 1}
                        </Typography>
                      </Box>
                      {formData.innengeraete.length > 1 && (
                        <IconButton 
                          color="error" 
                          onClick={() => handleRemoveInnengeraet(index)}
                          size="small"
                          sx={{ 
                            '&:hover': { 
                              backgroundColor: '#ffebee',
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Box>
                    
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                      {/* MONTAGEORT - Erstes Feld mit ID für Auto-Focus */}
                      <Grid item xs={12}>
                        <TextField
                          id={`montageort-${index}`}
                          fullWidth
                          label="Montageort (z.B. Schlafzimmer, Büro, Küche)"
                          value={geraet.montageort || ''}
                          onChange={(e) => handleInnengeraetChange(index, 'montageort', capitalizeFirst(e.target.value))}
                          placeholder="Schlafzimmer"
                          sx={{ mb: 1 }}
                          inputRef={index === 0 ? panel2FirstFieldRef : null}
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Modellnummer"
                          value={geraet.modell}
                          onChange={(e) => handleInnengeraetChange(index, 'modell', e.target.value.toUpperCase())}
                          sx={{ minWidth: 200 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Seriennummer"
                          value={geraet.seriennummer}
                          onChange={(e) => handleInnengeraetChange(index, 'seriennummer', e.target.value.toUpperCase())}
                          sx={{ minWidth: 200 }}
                        />
                      </Grid>
                      
                      {/* Betriebsmodus Radio Buttons */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Thermostat sx={{ color: '#ff9800' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            Betriebsmodus
                          </Typography>
                        </Box>
                        <RadioGroup
                          row
                          value={geraet.betriebsmodus || 'kühlen'}
                          onChange={(e) => handleInnengeraetChange(index, 'betriebsmodus', e.target.value)}
                        >
                          <FormControlLabel 
                            value="kühlen" 
                            control={<Radio />} 
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AcUnit sx={{ color: '#2196f3', fontSize: 20 }} /> 
                                Kühlbetrieb
                              </Box>
                            } 
                          />
                          <FormControlLabel 
                            value="heizen" 
                            control={<Radio />} 
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Thermostat sx={{ color: '#ff9800', fontSize: 20 }} /> 
                                Heizbetrieb
                              </Box>
                            } 
                          />
                        </RadioGroup>
                        <Divider sx={{ mt: 1, mb: 2 }} />
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Ansaugtemperatur (°C)"
                          type="number"
                          value={geraet.ansaug_temp}
                          onChange={(e) => handleInnengeraetChange(index, 'ansaug_temp', e.target.value)}
                          sx={{ minWidth: 200 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Ausblastemperatur (°C)"
                          type="number"
                          value={geraet.ausblas_temp}
                          onChange={(e) => handleInnengeraetChange(index, 'ausblas_temp', e.target.value)}
                          sx={{ minWidth: 200 }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Air sx={{ color: '#1976d2', fontSize: 24 }} />
                          <FormControl fullWidth sx={{ minWidth: 200 }}>
                            <InputLabel>Lüfterstufe im Probelauf</InputLabel>
                            <Select
                              value={geraet.luefterstufe || ''}
                              onChange={(e) => handleInnengeraetChange(index, 'luefterstufe', e.target.value)}
                              label="Lüfterstufe im Probelauf"
                            >
                              <MenuItem value="">-- Keine Angabe --</MenuItem>
                              <MenuItem value="Stufe 1">Stufe 1</MenuItem>
                              <MenuItem value="Stufe 2">Stufe 2</MenuItem>
                              <MenuItem value="Stufe 3">Stufe 3</MenuItem>
                              <MenuItem value="Stufe 4">Stufe 4</MenuItem>
                              <MenuItem value="Stufe 5">Stufe 5</MenuItem>
                              <MenuItem value="Stufe 6">Stufe 6</MenuItem>
                              <MenuItem value="Stufe 7">Stufe 7</MenuItem>
                              <MenuItem value="Automatik">Automatik</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
                          ⚠️ Bitte höchste Lüfterstufe für Prüfung verwenden (3, 4, 5, 7 oder Automatik)
                        </Alert>
                      </Grid>
                      
                      {/* Fehlervalidierung für unrealistische Temperaturen */}
                      {(() => {
                        const tempError = checkTemperatureLogic(geraet);
                        if (!tempError) return null;
                        
                        return (
                          <Grid item xs={12}>
                            <Alert severity="error" sx={{ mt: 2, border: '2px solid #f44336', fontWeight: 'bold' }}>
                              ⚠️ Werte überprüfen: {tempError}
                            </Alert>
                          </Grid>
                        );
                      })()}
                      
                      {/* Effizienzeinschätzung Label */}
                      {(() => {
                        const efficiency = calculateEfficiency(geraet);
                        if (!efficiency) return null;
                        
                        return (
                          <Grid item xs={12}>
                            <Card sx={{ 
                              backgroundColor: '#f5f5f5', 
                              border: `2px solid ${efficiency.color}`,
                              mt: 2
                            }}>
                              <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                  <CheckCircle sx={{ color: efficiency.color, fontSize: 28 }} />
                                  <Typography variant="h6" component="div">
                                    Effizienzeinschätzung
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <Typography variant="body1">
                                    ΔT: <strong>{efficiency.deltaT} K</strong>
                                  </Typography>
                                  <Box sx={{
                                    backgroundColor: efficiency.color,
                                    color: 'white',
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: '16px',
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase'
                                  }}>
                                    {efficiency.status}
                                  </Box>
                                  <Typography variant="body2" color="text.secondary">
                                    Sollbereich {efficiency.betriebsmodus === 'kühlen' ? 'Kühlen' : 'Heizen'}: {efficiency.sollbereich}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                  💡 Grober Schätzwert basierend auf Temperaturdifferenz bei höchster Lüfterstufe
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })()}
                      
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" gutterBottom>
                          <strong>Durchgeführte Arbeiten:</strong>
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={geraet.arbeiten.luftfilter}
                              onChange={() => handleInnengeraetArbeitenChange(index, 'luftfilter')}
                              inputProps={{ 
                                tabIndex: 0,
                                onKeyDown: (e) => handleCheckboxKeyDown(e, () => handleInnengeraetArbeitenChange(index, 'luftfilter'))
                              }}
                              sx={{
                                color: geraet.arbeiten.luftfilter ? 'success.main' : 'default',
                                '&.Mui-checked': { color: 'success.main' }
                              }}
                            />
                          }
                          label="Luftfilter prüfen und reinigen"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={geraet.arbeiten.verdampfer}
                              onChange={() => handleInnengeraetArbeitenChange(index, 'verdampfer')}
                              onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleInnengeraetArbeitenChange(index, 'verdampfer'))}
                              inputProps={{ tabIndex: 0 }}
                              sx={{
                                color: geraet.arbeiten.verdampfer ? 'success.main' : 'default',
                                '&.Mui-checked': { color: 'success.main' }
                              }}
                            />
                          }
                          label="Verdampfer prüfen und reinigen"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={geraet.arbeiten.kondenswasserpumpe}
                              onChange={() => handleInnengeraetArbeitenChange(index, 'kondenswasserpumpe')}
                              onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleInnengeraetArbeitenChange(index, 'kondenswasserpumpe'))}
                              inputProps={{ tabIndex: 0 }}
                              sx={{
                                color: geraet.arbeiten.kondenswasserpumpe ? 'success.main' : 'default',
                                '&.Mui-checked': { color: 'success.main' }
                              }}
                            />
                          }
                          label="Kondenswasserpumpe prüfen/reinigen"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={geraet.arbeiten.kondenswasserwanne}
                              onChange={() => handleInnengeraetArbeitenChange(index, 'kondenswasserwanne')}
                              onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleInnengeraetArbeitenChange(index, 'kondenswasserwanne'))}
                              inputProps={{ tabIndex: 0 }}
                              sx={{
                                color: geraet.arbeiten.kondenswasserwanne ? 'success.main' : 'default',
                                '&.Mui-checked': { color: 'success.main' }
                              }}
                            />
                          }
                          label="Kondenswasserwanne reinigen und desinfizieren"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={geraet.arbeiten.kondenswasserablauf_geprueft}
                              onChange={() => handleInnengeraetArbeitenChange(index, 'kondenswasserablauf_geprueft')}
                              onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleInnengeraetArbeitenChange(index, 'kondenswasserablauf_geprueft'))}
                              inputProps={{ tabIndex: 0 }}
                              sx={{
                                color: geraet.arbeiten.kondenswasserablauf_geprueft ? 'success.main' : 'default',
                                '&.Mui-checked': { color: 'success.main' }
                              }}
                            />
                          }
                          label="Kondenswasserablauf geprüft"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={geraet.arbeiten.elektrische_anschluesse}
                              onChange={() => handleInnengeraetArbeitenChange(index, 'elektrische_anschluesse')}
                              onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleInnengeraetArbeitenChange(index, 'elektrische_anschluesse'))}
                              inputProps={{ tabIndex: 0 }}
                              sx={{
                                color: geraet.arbeiten.elektrische_anschluesse ? 'success.main' : 'default',
                                '&.Mui-checked': { color: 'success.main' }
                              }}
                            />
                          }
                          label="Elektrische Anschlüsse und Isolierungen prüfen"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={geraet.arbeiten.geblaesemotor}
                              onChange={() => handleInnengeraetArbeitenChange(index, 'geblaesemotor')}
                              onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleInnengeraetArbeitenChange(index, 'geblaesemotor'))}
                              inputProps={{ tabIndex: 0 }}
                              sx={{
                                color: geraet.arbeiten.geblaesemotor ? 'success.main' : 'default',
                                '&.Mui-checked': { color: 'success.main' }
                              }}
                            />
                          }
                          label="Gebläsemotor und Flügel prüfen"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={geraet.arbeiten.ausblaslamellen}
                              onChange={() => handleInnengeraetArbeitenChange(index, 'ausblaslamellen')}
                              onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleInnengeraetArbeitenChange(index, 'ausblaslamellen'))}
                              inputProps={{ tabIndex: 0 }}
                              sx={{
                                color: geraet.arbeiten.ausblaslamellen ? 'success.main' : 'default',
                                '&.Mui-checked': { color: 'success.main' }
                              }}
                            />
                          }
                          label="Ausblaslamellen prüfen und reinigen"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={geraet.arbeiten.gehaeuse}
                              onChange={() => handleInnengeraetArbeitenChange(index, 'gehaeuse')}
                              onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleInnengeraetArbeitenChange(index, 'gehaeuse'))}
                              inputProps={{ tabIndex: 0 }}
                              sx={{
                                color: geraet.arbeiten.gehaeuse ? 'success.main' : 'default',
                                '&.Mui-checked': { color: 'success.main' }
                              }}
                            />
                          }
                          label="Gehäuse prüfen und reinigen"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
              
              {formData.innengeraete.length < 20 && (
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={handleAddInnengeraet}
                  fullWidth
                >
                  Innengerät hinzufügen ({formData.innengeraete.length}/20)
                </Button>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Accordion 3: Außengerät */}
        <Accordion 
          expanded={expanded === 'panel3'} 
          onChange={handleAccordionChange('panel3')}
          sx={{ 
            mb: 2,
            boxShadow: 3,
            '&:before': { display: 'none' },
            borderRadius: '8px !important'
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              backgroundColor: '#ed6c02',
              color: 'white',
              borderRadius: '8px 8px 0 0',
              '&:hover': { backgroundColor: '#e65100' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeviceThermostat />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Außengerät</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'white' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Modellnummer"
                  value={formData.aussengeraet.modell}
                  onChange={(e) => handleAussengeraetChange('modell', e.target.value.toUpperCase())}
                  sx={{ minWidth: 200 }}
                  inputRef={panel3FirstFieldRef}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Seriennummer"
                  value={formData.aussengeraet.seriennummer}
                  onChange={(e) => handleAussengeraetChange('seriennummer', e.target.value.toUpperCase())}
                  sx={{ minWidth: 200 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DeviceThermostat fontSize="small" sx={{ color: '#2196f3' }} />
                      Außentemperatur (°C)
                    </Box>
                  }
                  type="number"
                  value={formData.aussengeraet.aussen_temp}
                  onChange={(e) => handleAussengeraetChange('aussen_temp', e.target.value)}
                  sx={{ minWidth: 200 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ElectricBolt fontSize="small" sx={{ color: '#ff9800' }} />
                      Stromaufnahme (A)
                    </Box>
                  }
                  type="number"
                  value={formData.aussengeraet.stromaufnahme}
                  onChange={(e) => handleAussengeraetChange('stromaufnahme', e.target.value)}
                  sx={{ minWidth: 200 }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Durchgeführte Arbeiten:</strong>
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.aussengeraet.arbeiten.waermetauscher}
                      onChange={() => handleAussengeraetArbeitenChange('waermetauscher')}
                      onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleAussengeraetArbeitenChange('waermetauscher'))}
                      inputProps={{ tabIndex: 0 }}
                      sx={{
                        color: formData.aussengeraet.arbeiten.waermetauscher ? 'success.main' : 'default',
                        '&.Mui-checked': { color: 'success.main' }
                      }}
                    />
                  }
                  label="Wärmetauscher prüfen und reinigen"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.aussengeraet.arbeiten.kaeltetechnische_anschluesse}
                      onChange={() => handleAussengeraetArbeitenChange('kaeltetechnische_anschluesse')}
                      onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleAussengeraetArbeitenChange('kaeltetechnische_anschluesse'))}
                      inputProps={{ tabIndex: 0 }}
                      sx={{
                        color: formData.aussengeraet.arbeiten.kaeltetechnische_anschluesse ? 'success.main' : 'default',
                        '&.Mui-checked': { color: 'success.main' }
                      }}
                    />
                  }
                  label="Kältetechnische Anschlüsse auf Dichtheit prüfen"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.aussengeraet.arbeiten.kondensatwanne}
                      onChange={() => handleAussengeraetArbeitenChange('kondensatwanne')}
                      onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleAussengeraetArbeitenChange('kondensatwanne'))}
                      inputProps={{ tabIndex: 0 }}
                      sx={{
                        color: formData.aussengeraet.arbeiten.kondensatwanne ? 'success.main' : 'default',
                        '&.Mui-checked': { color: 'success.main' }
                      }}
                    />
                  }
                  label="Kondensatwanne und Ablauf prüfen und reinigen"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.aussengeraet.arbeiten.innenraum}
                      onChange={() => handleAussengeraetArbeitenChange('innenraum')}
                      onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleAussengeraetArbeitenChange('innenraum'))}
                      inputProps={{ tabIndex: 0 }}
                      sx={{
                        color: formData.aussengeraet.arbeiten.innenraum ? 'success.main' : 'default',
                        '&.Mui-checked': { color: 'success.main' }
                      }}
                    />
                  }
                  label="Innenraum prüfen und reinigen"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.aussengeraet.arbeiten.lueftermotor}
                      onChange={() => handleAussengeraetArbeitenChange('lueftermotor')}
                      onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleAussengeraetArbeitenChange('lueftermotor'))}
                      inputProps={{ tabIndex: 0 }}
                      sx={{
                        color: formData.aussengeraet.arbeiten.lueftermotor ? 'success.main' : 'default',
                        '&.Mui-checked': { color: 'success.main' }
                      }}
                    />
                  }
                  label="Lüftermotor auf Unwucht prüfen, Flügel ggf. reinigen"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.aussengeraet.arbeiten.isolierung}
                      onChange={() => handleAussengeraetArbeitenChange('isolierung')}
                      onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleAussengeraetArbeitenChange('isolierung'))}
                      inputProps={{ tabIndex: 0 }}
                      sx={{
                        color: formData.aussengeraet.arbeiten.isolierung ? 'success.main' : 'default',
                        '&.Mui-checked': { color: 'success.main' }
                      }}
                    />
                  }
                  label="Isolierung und Anschlusskabel prüfen"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.aussengeraet.arbeiten.elektrische_anschluesse}
                      onChange={() => handleAussengeraetArbeitenChange('elektrische_anschluesse')}
                      onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleAussengeraetArbeitenChange('elektrische_anschluesse'))}
                      inputProps={{ tabIndex: 0 }}
                      sx={{
                        color: formData.aussengeraet.arbeiten.elektrische_anschluesse ? 'success.main' : 'default',
                        '&.Mui-checked': { color: 'success.main' }
                      }}
                    />
                  }
                  label="Elektrische Anschlüsse und Klemmschrauben prüfen"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.aussengeraet.arbeiten.sicherheitseinrichtungen}
                      onChange={() => handleAussengeraetArbeitenChange('sicherheitseinrichtungen')}
                      onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleAussengeraetArbeitenChange('sicherheitseinrichtungen'))}
                      inputProps={{ tabIndex: 0 }}
                      sx={{
                        color: formData.aussengeraet.arbeiten.sicherheitseinrichtungen ? 'success.main' : 'default',
                        '&.Mui-checked': { color: 'success.main' }
                      }}
                    />
                  }
                  label="Sicherheitseinrichtungen (Druckschalter) testen"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.aussengeraet.arbeiten.kompressor}
                      onChange={() => handleAussengeraetArbeitenChange('kompressor')}
                      onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleAussengeraetArbeitenChange('kompressor'))}
                      inputProps={{ tabIndex: 0 }}
                      sx={{
                        color: formData.aussengeraet.arbeiten.kompressor ? 'success.main' : 'default',
                        '&.Mui-checked': { color: 'success.main' }
                      }}
                    />
                  }
                  label="Kompressor auf Unwucht/Geräusche prüfen"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.aussengeraet.arbeiten.kaeltemitteldruecke}
                      onChange={() => handleAussengeraetArbeitenChange('kaeltemitteldruecke')}
                      onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleAussengeraetArbeitenChange('kaeltemitteldruecke'))}
                      inputProps={{ tabIndex: 0 }}
                      sx={{
                        color: formData.aussengeraet.arbeiten.kaeltemitteldruecke ? 'success.main' : 'default',
                        '&.Mui-checked': { color: 'success.main' }
                      }}
                    />
                  }
                  label="Kältemitteldrücke und Temperaturen prüfen"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.aussengeraet.arbeiten.leistungsmessung}
                      onChange={() => handleAussengeraetArbeitenChange('leistungsmessung')}
                      onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleAussengeraetArbeitenChange('leistungsmessung'))}
                      inputProps={{ tabIndex: 0 }}
                      sx={{
                        color: formData.aussengeraet.arbeiten.leistungsmessung ? 'success.main' : 'default',
                        '&.Mui-checked': { color: 'success.main' }
                      }}
                    />
                  }
                  label="Stromaufnahme und Leistungsmessung"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Accordion 4: Dichteprüfung & Kältemittel */}
        <Accordion 
          expanded={expanded === 'panel4'} 
          onChange={handleAccordionChange('panel4')}
          sx={{ 
            mb: 2,
            boxShadow: 3,
            '&:before': { display: 'none' },
            borderRadius: '8px !important'
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              backgroundColor: '#9c27b0',
              color: 'white',
              borderRadius: '8px 8px 0 0',
              '&:hover': { backgroundColor: '#7b1fa2' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Science />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Dichteprüfung & Kältemittel</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'white' }}>
            <Grid container spacing={3}>
              {/* Dichteprüfung notwendig */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ minWidth: 200 }}>
                  <InputLabel>Dichtheitsprüfung notwendig?</InputLabel>
                  <Select
                    value={formData.dichtepr_notwendig}
                    onChange={(e) => handleChange('dichtepr_notwendig', e.target.value)}
                    label="Dichtheitsprüfung notwendig?"
                  >
                    <MenuItem value="nein">Nein</MenuItem>
                    <MenuItem value="ja">Ja</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* NUR anzeigen wenn Dichteprüfung notwendig = JA */}
              {formData.dichtepr_notwendig === 'ja' && (
                <>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth sx={{ minWidth: 200 }}>
                      <InputLabel>Prüfungsintervall (Monate)</InputLabel>
                      <Select
                        value={formData.pruefintervall}
                        onChange={(e) => handleChange('pruefintervall', e.target.value)}
                        label="Prüfungsintervall (Monate)"
                      >
                        <MenuItem value="3">3 Monate</MenuItem>
                        <MenuItem value="6">6 Monate</MenuItem>
                        <MenuItem value="12">12 Monate</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth sx={{ minWidth: 200 }}>
                      <InputLabel>Dichteprüfung durchgeführt?</InputLabel>
                      <Select
                        value={formData.dichtepr_durchgefuehrt}
                        onChange={(e) => handleChange('dichtepr_durchgefuehrt', e.target.value)}
                        label="Dichteprüfung durchgeführt?"
                      >
                        <MenuItem value="nein">Nein</MenuItem>
                        <MenuItem value="ja">Ja</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* NUR anzeigen wenn durchgeführt = JA */}
                  {formData.dichtepr_durchgefuehrt === 'ja' && (
                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth sx={{ minWidth: 200 }}>
                        <InputLabel>Ergebnis</InputLabel>
                        <Select
                          value={formData.anlage_status}
                          onChange={(e) => handleChange('anlage_status', e.target.value)}
                          label="Ergebnis"
                        >
                          <MenuItem value="dicht">Dicht</MenuItem>
                          <MenuItem value="undicht">Undicht</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </>
              )}
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Kältemittel:</strong>
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={8}>
                <FormControl fullWidth>
                  <InputLabel>Kältemittel</InputLabel>
                  <Select
                    value={formData.kaeltemittel}
                    onChange={(e) => handleChange('kaeltemittel', e.target.value)}
                    label="Kältemittel"
                  >
                    <MenuItem value="R32">R32</MenuItem>
                    <MenuItem value="R410A">R410A</MenuItem>
                    <MenuItem value="R134a">R134a</MenuItem>
                    <MenuItem value="R407C">R407C</MenuItem>
                    <MenuItem value="R290">R290 (Propan)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Füllmenge lt. Typenschild (kg)"
                  type="number"
                  inputProps={{ step: '0.01' }}
                  value={formData.fuellmenge_typenschild}
                  onChange={(e) => handleChange('fuellmenge_typenschild', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nachfüllmenge (kg)"
                  type="number"
                  inputProps={{ step: '0.01' }}
                  value={formData.nachfuellmenge}
                  onChange={(e) => handleChange('nachfuellmenge', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Gesamtfüllmenge (kg)"
                  type="number"
                  value={formData.gesamtfuellmenge}
                  InputProps={{ readOnly: true }}
                  disabled
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Accordion 5: Hinweise, Bilder & Unterschriften - NEU STRUKTURIERT */}
        <Accordion 
          expanded={expanded === 'panel5'} 
          onChange={handleAccordionChange('panel5')}
          sx={{ border: '2px solid #4caf50', borderRadius: 1 }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ backgroundColor: '#e8f5e9' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Inventory color="success" />
              <Typography variant="h6">5. Hinweise, Bilder & Unterschriften</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              
              {/* 1. HINWEISE - 100% Breite */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Info color="info" />
                  Hinweise
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  label="Zusätzliche Hinweise oder Bemerkungen"
                  value={formData.hinweise}
                  onChange={(e) => handleChange('hinweise', e.target.value)}
                  placeholder="Z.B. besondere Vorkommnisse, Empfehlungen, durchgeführte Reparaturen..."
                  inputRef={panel5FirstFieldRef}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fafafa',
                      fontSize: '1.1rem'
                    }
                  }}
                />
              </Box>
              
              <Divider />
              
              {/* 2. BILDER/FOTOS - Neue Zeile */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PhotoCamera color="success" />
                  Fotos (max. 5 Bilder)
                </Typography>
                <ImageUploader
                  images={formData.bilder || []}
                  onImagesSelected={(newImages) => {
                    handleChange('bilder', newImages);
                  }}
                  maxImages={5}
                />
              </Box>
              
              <Divider />
              
              {/* 3. UNTERSCHRIFT MONTEUR - Neue Zeile */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Draw color="primary" />
                  Unterschrift Monteur *
                </Typography>
                <Box sx={{ 
                  border: '2px dashed #2196f3', 
                  borderRadius: 2, 
                  p: 3, 
                  textAlign: 'center',
                  backgroundColor: formData.monteur_unterschrift ? '#e3f2fd' : '#fafafa',
                  minHeight: 150
                }}>
                  {formData.monteur_unterschrift ? (
                    <Box>
                      <img 
                        src={formData.monteur_unterschrift} 
                        alt="Monteur Unterschrift" 
                        style={{ maxWidth: '100%', maxHeight: '120px', border: '1px solid #ddd' }}
                      />
                      <Box sx={{ mt: 2 }}>
                        <Button 
                          variant="outlined" 
                          onClick={() => setMonteurSigDialog(true)}
                        >
                          Neu unterschreiben
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Button 
                      variant="contained" 
                      size="large"
                      startIcon={<Draw />}
                      onClick={() => setMonteurSigDialog(true)}
                    >
                      Jetzt unterschreiben
                    </Button>
                  )}
                </Box>
              </Box>
              
              <Divider />
              
              {/* 4. KUNDENZUFRIEDENHEIT - Neue Zeile */}
              <Box>
                <Card sx={{ p: 3, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CheckCircle color="success" />
                    Kundenzufriedenheit
                  </Typography>
                  
                  <Typography variant="body1" gutterBottom fontWeight="medium" sx={{ mb: 2 }}>
                    Wurde die Wartung zu Ihrer Zufriedenheit durchgeführt?
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.kunde_zufrieden === 'ja'}
                          onChange={() => {
                            handleChange('kunde_zufrieden', 'ja');
                            handleChange('kunde_zufrieden_begruendung', '');
                          }}
                          onKeyDown={(e) => handleCheckboxKeyDown(e, () => {
                            handleChange('kunde_zufrieden', 'ja');
                            handleChange('kunde_zufrieden_begruendung', '');
                          })}
                          inputProps={{ tabIndex: 0 }}
                          sx={{
                            color: 'success.light',
                            '&.Mui-checked': { color: 'success.main' }
                          }}
                        />
                      }
                      label={<Typography variant="body1"><strong>Ja</strong>, ich bin zufrieden</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.kunde_zufrieden === 'nein'}
                          onChange={() => handleChange('kunde_zufrieden', 'nein')}
                          onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleChange('kunde_zufrieden', 'nein'))}
                          inputProps={{ tabIndex: 0 }}
                          sx={{
                            color: 'error.light',
                            '&.Mui-checked': { color: 'error.main' }
                          }}
                        />
                      }
                      label={<Typography variant="body1"><strong>Nein</strong>, ich bin nicht zufrieden</Typography>}
                    />
                  </Box>
                  
                  {/* Begründung nur bei "nein" */}
                  {formData.kunde_zufrieden === 'nein' && (
                    <Box sx={{ mt: 3 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Begründung (Pflichtfeld)"
                        value={formData.kunde_zufrieden_begruendung || ''}
                        onChange={(e) => handleChange('kunde_zufrieden_begruendung', e.target.value)}
                        placeholder="Bitte geben Sie an, warum Sie nicht zufrieden sind..."
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff3cd',
                            borderColor: '#ffc107'
                          }
                        }}
                      />
                    </Box>
                  )}
                </Card>
              </Box>
              
              <Divider />
              
              {/* 5. NAME KUNDE (Textfeld) - Neue Zeile */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Name des Unterzeichners (optional)
                </Typography>
                <TextField
                  fullWidth
                  label="Name des Kunden (falls abweichend)"
                  value={formData.kunde_name_unterschrift || ''}
                  onChange={(e) => handleChange('kunde_name_unterschrift', capitalizeFirst(e.target.value))}
                  placeholder="Z.B. wenn eine andere Person unterschreibt"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fafafa'
                    }
                  }}
                />
              </Box>
              
              <Divider />
              
              {/* EMAIL-EMPFÄNGER */}
              <Box sx={{ p: 3, border: '2px solid #2196f3', borderRadius: 2, backgroundColor: '#e3f2fd' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  📧 Email-Empfänger
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  label="Kunden-Email"
                  value={formData.kunde_email || ''}
                  onChange={(e) => handleChange('kunde_email', e.target.value)}
                  placeholder="kunde@example.com"
                  helperText="Das Protokoll wird an diese Email-Adresse gesendet"
                  sx={{ 
                    minWidth: 200,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff'
                    }
                  }}
                />
              </Box>
              
              <Divider />
              
              {/* 6. UNTERSCHRIFT KUNDE - Neue Zeile */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Draw color="success" />
                  Unterschrift Kunde *
                </Typography>
                <Box sx={{ 
                  border: '2px dashed #4caf50', 
                  borderRadius: 2, 
                  p: 3, 
                  textAlign: 'center',
                  backgroundColor: formData.kunde_unterschrift ? '#e8f5e9' : '#fafafa',
                  minHeight: 150
                }}>
                  {formData.kunde_unterschrift ? (
                    <Box>
                      <img 
                        src={formData.kunde_unterschrift} 
                        alt="Kunde Unterschrift" 
                        style={{ maxWidth: '100%', maxHeight: '120px', border: '1px solid #ddd' }}
                      />
                      <Box sx={{ mt: 2 }}>
                        <Button 
                          variant="outlined"
                          color="success"
                          onClick={() => setKundeSigDialog(true)}
                        >
                          Neu unterschreiben
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Button 
                      variant="contained" 
                      color="success"
                      size="large"
                      startIcon={<Draw />}
                      onClick={() => setKundeSigDialog(true)}
                    >
                      Jetzt unterschreiben
                    </Button>
                  )}
                </Box>
              </Box>
              
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* MONTEUR UNTERSCHRIFT POPUP */}
        <Dialog 
          open={monteurSigDialog} 
          onClose={() => {
            // Nicht schließen wenn keine Unterschrift
            if (!formData.monteur_unterschrift) {
              alert('Bitte unterschreiben Sie oder brechen Sie ab');
              return;
            }
            setMonteurSigDialog(false);
          }}
          maxWidth="md"
          fullWidth
          fullScreen={window.innerWidth < 600}
        >
          <DialogTitle>Monteur Unterschrift</DialogTitle>
          <DialogContent sx={{ minHeight: 400, p: 3 }}>
            <SignatureCanvas
              value={formData.monteur_unterschrift}
              onChange={(signature) => {
                console.log('Monteur signature changed:', signature ? 'YES' : 'NO');
                handleChange('monteur_unterschrift', signature);
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => {
                handleChange('monteur_unterschrift', '');
                setMonteurSigDialog(false);
              }}
              variant="outlined"
            >
              Abbrechen
            </Button>
            <Button 
              variant="contained" 
              onClick={() => {
                if (!formData.monteur_unterschrift) {
                  alert('Bitte unterschreiben Sie erst!');
                  return;
                }
                console.log('Saving monteur signature:', formData.monteur_unterschrift);
                setMonteurSigDialog(false);
              }}
              disabled={!formData.monteur_unterschrift}
            >
              Speichern
            </Button>
          </DialogActions>
        </Dialog>

        {/* KUNDE UNTERSCHRIFT POPUP */}
        <Dialog 
          open={kundeSigDialog} 
          onClose={() => {
            if (!formData.kunde_unterschrift) {
              alert('Bitte unterschreiben Sie oder brechen Sie ab');
              return;
            }
            setKundeSigDialog(false);
          }}
          maxWidth="md"
          fullWidth
          fullScreen={window.innerWidth < 600}
        >
          <DialogTitle>Kunde Unterschrift</DialogTitle>
          <DialogContent sx={{ minHeight: 400, p: 3 }}>
            <SignatureCanvas
              value={formData.kunde_unterschrift}
              onChange={(signature) => {
                console.log('Kunde signature changed:', signature ? 'YES' : 'NO');
                handleChange('kunde_unterschrift', signature);
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => {
                handleChange('kunde_unterschrift', '');
                setKundeSigDialog(false);
              }}
              variant="outlined"
            >
              Abbrechen
            </Button>
            <Button 
              variant="contained"
              color="success"
              onClick={() => {
                if (!formData.kunde_unterschrift) {
                  alert('Bitte unterschreiben Sie erst!');
                  return;
                }
                console.log('Saving kunde signature:', formData.kunde_unterschrift);
                setKundeSigDialog(false);
              }}
              disabled={!formData.kunde_unterschrift}
            >
              Speichern
            </Button>
          </DialogActions>
        </Dialog>

        {/* Action Buttons */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate('/protocol/klima-list')}
            disabled={loading}
          >
            ABBRECHEN
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Visibility />}
            onClick={handlePreview}
            disabled={loading}
          >
            PDF VORSCHAU
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<Save />}
            onClick={handleSaveDraft}
            disabled={loading}
          >
            ALS ENTWURF SPEICHERN
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={loading ? <CircularProgress size={20} /> : <Send />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'WIRD VERSENDET...' : 'SPEICHERN & VERSENDEN'}
          </Button>
        </Box>
      </Container>

      {/* Auto-Save Wiederherstellungs-Dialog */}
      <Dialog 
        open={showRestoreDialog} 
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#1976d2', color: 'white' }}>
          💾 Begonnenes Formular gefunden
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Es wurde ein automatisch gespeichertes Formular gefunden. Möchten Sie Ihre Arbeit fortsetzen?
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Das Formular wurde automatisch gespeichert, als Sie das letzte Mal daran gearbeitet haben.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDiscardAutoSave}
            color="error"
            variant="outlined"
          >
            Verwerfen & neu beginnen
          </Button>
          <Button 
            onClick={handleRestoreAutoSave}
            color="success"
            variant="contained"
            autoFocus
          >
            Wiederherstellen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
